// Lightweight fuzzy search utilities (no dependencies)
// Simple Levenshtein distance implementation for typo tolerance

export function levenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = Array(b.length + 1)
    .fill(null)
    .map(() => Array(a.length + 1).fill(null));

  for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= b.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // insertion
        matrix[j - 1][i] + 1, // deletion
        matrix[j - 1][i - 1] + substitutionCost, // substitution
      );
    }
  }

  return matrix[b.length][a.length];
}

export interface FuzzyMatch {
  text: string;
  score: number;
  isExact: boolean;
  startsWith: boolean;
}

export function fuzzyScore(query: string, target: string): FuzzyMatch {
  const q = query.toLowerCase().trim();
  const t = target.toLowerCase();

  // Exact match
  if (t === q) {
    return { text: target, score: 1000, isExact: true, startsWith: true };
  }

  // Starts with (high priority)
  if (t.startsWith(q)) {
    return { text: target, score: 900, isExact: false, startsWith: true };
  }

  // Contains substring
  if (t.includes(q)) {
    return { text: target, score: 800, isExact: false, startsWith: false };
  }

  // Fuzzy matching for typos (only for queries >= 4 chars)
  if (q.length >= 4) {
    const distance = levenshteinDistance(q, t);
    if (distance <= 1) {
      return { text: target, score: 700 - distance * 100, isExact: false, startsWith: false };
    }

    // Check if target contains a word that's close to query
    const words = t.split(/[.\-_\s]+/);
    for (const word of words) {
      if (word.length >= 3) {
        const wordDistance = levenshteinDistance(q, word);
        if (wordDistance <= 1) {
          return {
            text: target,
            score: 600 - wordDistance * 100,
            isExact: false,
            startsWith: false,
          };
        }
      }
    }
  }

  return { text: target, score: 0, isExact: false, startsWith: false };
}

export function highlightQuery(text: string, query: string): string {
  if (!query.trim()) return text;

  const q = query.toLowerCase().trim();
  const lowerText = text.toLowerCase();

  // Find the best match position
  let matchStart = lowerText.indexOf(q);
  if (matchStart === -1) {
    // Try fuzzy highlighting for single character differences
    if (q.length >= 4) {
      for (let i = 0; i <= lowerText.length - q.length; i++) {
        const substr = lowerText.substring(i, i + q.length);
        if (levenshteinDistance(q, substr) <= 1) {
          matchStart = i;
          break;
        }
      }
    }
  }

  if (matchStart === -1) return text;

  const matchEnd = matchStart + q.length;
  return (
    text.substring(0, matchStart) +
    '<mark class="bg-primary/20 text-primary font-medium">' +
    text.substring(matchStart, matchEnd) +
    '</mark>' +
    text.substring(matchEnd)
  );
}
